import { createEffect, onCleanup, untrack } from "solid-js";
import type { ServerConnection, useServer } from "@/context/server";
import { machineRouteManager, type MachineRoute } from "@/fleet/machine-route-manager";
import { installMachineTransport } from "@/fleet/machine-transport";
import { ServerUnavailableError } from "@/transport/server";
import { abortable } from "@/utils/abortable";
import { pageIsVisible } from "@/utils/network-schedule";
import { machineBaseUrl, type FleetMachine } from "./fleet-machine";

const trimURL = (url: string) => url.replace(/\/+$/, "");
const publicRouteURL = (machine: FleetMachine, url: string) =>
  trimURL(url) === trimURL(machine.public_url) || !!machine.public_routes?.some(route => trimURL(route.url) === trimURL(url));

/**
 * Connections to account machines: building transports, recovering them onto
 * another route after failures, and moving a working public route to a better
 * one. Route work ends with the owner and pauses with the page, since
 * unloading fails the page's own requests, which selection would report as
 * route failures.
 */
export function createFleetRouteController(options: {
  server: ReturnType<typeof useServer>;
  machines: () => FleetMachine[] | undefined;
  /** Machines on screen; only they look for a better route. */
  shown: () => readonly string[];
  ensureMachineAccess: (name: string, rejectedToken?: string) => Promise<FleetMachine | undefined>;
  enabled: boolean;
}) {
  const { server, ensureMachineAccess } = options;
  const find = (name: string | undefined) => options.machines()?.find((item) => item.name === name);

  let routeLifetime = new AbortController();
  const pageHide = () => routeLifetime.abort();
  const pageShow = () => { if (routeLifetime.signal.aborted) routeLifetime = new AbortController(); };
  // Mobile browsers can fire pagehide for a backgrounded app and resume it without pageshow.
  const visibilityChange = () => { if (pageIsVisible()) pageShow(); };
  window.addEventListener("pagehide", pageHide);
  window.addEventListener("pageshow", pageShow);
  document.addEventListener("visibilitychange", visibilityChange);
  const routeSignal = (signal?: AbortSignal) => signal ? AbortSignal.any([routeLifetime.signal, signal]) : routeLifetime.signal;

  const publishRoute = (name: string, route: MachineRoute) => {
    const current = find(name);
    if (route.kind === "unavailable" || !current?.accessToken || machineRouteManager.peek(current)?.url !== route.url) return;
    return installMachineTransport(server, current, machineConnection(current, route.url), route.trigger);
  };

  const machineConnection = (machine: FleetMachine, url = machineBaseUrl(machine)): ServerConnection.HttpBase => ({
    url,
    identityURL: machine.public_url.replace(/\/+$/, ""),
    expectedMachineName: machine.name,
    accessToken: machine.accessToken,
    accessTokenExpiresAt: machine.accessTokenExpiresAt,
    // Public routes pass through relays, so their requests are sealed; loopback is not.
    e2eKey: publicRouteURL(machine, url) ? machine.e2e_public_key : undefined,
    prepare: async (rejectedToken) => {
      const latest = await ensureMachineAccess(machine.name, rejectedToken);
      if (!latest?.accessToken || !machineBaseUrl(latest)) throw new ServerUnavailableError();
      if (machineRouteManager.localOnlyURL(latest) && machineRouteManager.observed(latest)?.kind === "unavailable") throw new ServerUnavailableError();
      const selected = machineRouteManager.peek(latest);
      return machineConnection(latest, selected?.url ?? machineBaseUrl(latest));
    },
    recover: async (trigger = "health-check", callerSignal, recoverOptions) => {
      const signal = routeSignal(callerSignal);
      try {
        signal.throwIfAborted();
        const latest = await abortable(ensureMachineAccess(machine.name), signal);
        if (!latest?.accessToken) return undefined;
        const selected = machineRouteManager.peek(latest);
        // A delayed failure from an old socket must not undo another caller's recovery.
        if (selected && selected.url !== url) return publishRoute(latest.name, selected)?.http;
        const streamFailure = trigger === "sse-error" || trigger === "sse-stall" || trigger === "pty-error";
        let recovered: ServerConnection.HttpBase | undefined;
        await machineRouteManager.reselect(latest, {
          trigger, signal, avoidUrl: streamFailure || recoverOptions?.avoidCurrent ? url : undefined,
          // A stalled read leaves a route only when the route itself fails, not for a slow handler.
          keepUrl: trigger === "request-stall" ? url : undefined,
          publish: route => { recovered = publishRoute(latest.name, route)?.http; },
        });
        return recovered;
      } catch (error) {
        if (!signal.aborted) throw error;
      }
    },
  });

  /** Grants access to an account machine and installs its best route. */
  const connect = async (name: string, signal: AbortSignal) => {
    try {
      const machine = await abortable(ensureMachineAccess(name), signal);
      if (!machine?.accessToken) return undefined;
      let installed: ReturnType<typeof publishRoute>;
      await machineRouteManager.select(machine, { signal, publish: route => { installed = publishRoute(name, route); } });
      return installed;
    } catch (error) {
      if (!signal.aborted) throw error;
    }
  };

  const discoveries = new Map<string, { controller: AbortController; machine: FleetMachine; route: MachineRoute }>();
  /** Stops looking for a better route for `name`, or for every machine. */
  const cancelDiscovery = (name?: string) => {
    for (const [machine, discovery] of discoveries) {
      if (name !== undefined && machine !== name) continue;
      discovery.controller.abort();
      discoveries.delete(machine);
    }
  };
  createEffect(() => {
    const names = options.shown();
    // Tracked whether or not a discovery is running, so one started later is checked too.
    options.machines();
    for (const [name, discovery] of discoveries) {
      const current = find(name);
      if (!names.includes(name) || !current || current.accessToken !== discovery.machine.accessToken ||
        machineRouteManager.peek(current) !== discovery.route) {
        cancelDiscovery(name);
      }
    }
  });
  // Moves a shown machine's working public route to a better one when it is
  // healthy: loopback (only probed when `local`), the preferred public route,
  // or one the control plane ranks higher.
  const discover = (name: string, local: boolean) => {
    const machine = find(name);
    const route = machine && machineRouteManager.peek(machine);
    if (!options.enabled || !machine || route?.kind !== "direct-public" || discoveries.has(name)) return;
    const controller = new AbortController();
    const discovery = { controller, machine, route };
    discoveries.set(name, discovery);
    void machineRouteManager.reconsider(machine, {
      local,
      signal: routeSignal(controller.signal),
      publish: route => { publishRoute(machine.name, route); },
    }).catch(() => undefined).finally(() => {
      if (discoveries.get(name) === discovery) discoveries.delete(name);
    });
  };
  const discoverBetterRoute = (local: boolean) => {
    for (const name of untrack(options.shown)) discover(name, local);
  };

  /**
   * Sets the public route this browser prefers for a machine. For a shown
   * machine, a change moves the connection at once when that route answers its
   * health check; machine event streams follow the new URL. Other machines use
   * it on their next selection.
   */
  const preferRoute = (name: string, url: string | undefined) => {
    if (!machineRouteManager.prefer(name, url) || !untrack(options.shown).includes(name)) return;
    cancelDiscovery(name);
    discover(name, false);
  };

  onCleanup(() => {
    window.removeEventListener("pagehide", pageHide);
    window.removeEventListener("pageshow", pageShow);
    document.removeEventListener("visibilitychange", visibilityChange);
    routeLifetime.abort();
    cancelDiscovery();
  });

  return { routeSignal, machineConnection, connect, discoverBetterRoute, cancelDiscovery, preferRoute };
}
